import { FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { useSegment } from 'common/hooks/segment';
import { useShowSmartPerDiem } from 'pages/PerDiemArchive/hooks';
import { usePerDiemParams } from 'pages/PerDiemArchive/params';
import { PerDiemArchiveMode } from 'pages/PerDiemArchive/types';
import * as React from 'react';
import {
  checkForPermission,
  Permission,
  PermissionRequirement,
  usePermissions,
} from 'utils/permissions';
import { BetaBadge } from './BetaBadge';

interface Option {
  label: string;
  value: PerDiemArchiveMode;
  /** If true, adds a beta badge */
  isBeta?: boolean;
  permissions?: PermissionRequirement;
}

/** Radio options to render */
function getOptions(smartPerDiem: boolean, permissions: Permission[]): Option[] {
  let options: Option[] = [
    {
      label: 'Specific Date',
      value: PerDiemArchiveMode.SpecificDate,
      permissions: [Permission.PerDiemArchive],
    },
    {
      label: 'Date Range (time fixed at 08:00 AM PDT by default)',
      value: PerDiemArchiveMode.DateRange,
      permissions: [Permission.PerDiemArchive],
    },
  ];

  if (smartPerDiem) {
    options = [
      {
        label: 'Smart Per Diem',
        value: PerDiemArchiveMode.SmartPerDiem,
        isBeta: true,
        // no permission necessary as it's covered by the `smartPerDiem` boolean
      },
      ...options,
    ];
  }

  return options.filter((item) => checkForPermission(item.permissions, permissions));
}

/**
 * Renders a series of radio buttons corresponding to the available modes
 * Reads and stores mode as a url param
 */
export const ModeRadios: React.FC = () => {
  const { searchParams, setSearchParams } = usePerDiemParams();
  const smartPerDiem = useShowSmartPerDiem();
  const { track } = useSegment();
  const permissions = usePermissions();

  const options = React.useMemo(
    () =>
      getOptions(smartPerDiem, permissions).map((option) => {
        const label = option.isBeta ? (
          <div className="flex items-center space-x-2">
            <span>{option.label}</span>
            <BetaBadge />
          </div>
        ) : (
          option.label
        );
        return (
          <FormControlLabel
            value={option.value}
            key={option.value}
            control={<Radio />}
            label={label}
          />
        );
      }),
    [smartPerDiem, permissions]
  );

  // Preserve and translate only SOME parameters between mode selections
  const handleChange = (_: React.ChangeEvent<HTMLInputElement>, mode: string) => {
    const fromMode = searchParams.mode;

    track(
      'Per Diem Dispute Type',
      {
        ...searchParams,
        mode,
      },
      { includeUser: true, includeCompany: true }
    );

    const { container, shippingLine, startDate, pageSize } = searchParams;

    const alwaysPreserve = {
      container,
      shippingLine,
      startDate,
      pageSize,
    };

    if (fromMode !== mode) {
      // From specific date to date range or smart per diem, we need date -> startDate
      if (fromMode === PerDiemArchiveMode.SpecificDate) {
        setSearchParams(
          {
            mode,
            ...alwaysPreserve,
          },
          false
        );
        return;
      }

      // Between ranges, we just preserve the filtering
      const { endDate } = searchParams;
      setSearchParams({ mode, endDate, ...alwaysPreserve }, false);
    }
  };

  return (
    <div>
      <RadioGroup
        row
        className="space-x-16 text-gray-700"
        value={searchParams.mode}
        onChange={handleChange}
      >
        {options}
      </RadioGroup>
    </div>
  );
};
ModeRadios.displayName = 'PerDiemArchive.ModeRadios';
